import { REGISTER_ACTION_MENU_KEY } from "@components/Sidebar/config/menu.keys";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ApplicationStatus } from "@constants/global.constants";
import { APPLICATION_STATUS, APPLICATION_TYPE } from "@constants/applicationStatus.constants";
import type { ChallanData } from "@features/CourseDetails/services/generateChallan.service";
import type { InstituteStatus } from "@services/instituteStatus/types";
import type { RecentApplicationData } from "@services/recentApplication/types";

export interface ConfigState {
	selectedMenuItem: string;
	applicationStatus: ApplicationStatus;
	applicationId: number | null;
	applicationUuid: string | null;
	/** Certified app UUID used to prefill attachments during renew-with-change draft */
	certifiedApplicationUuid: string | null;
	challanId: number | null;
	challanAmount: number | null;
	challanResponse: ChallanData | null;
	selectedInstituteId: string | number | null;
	selectedSessionId: string | number | null;
	instituteStatus: InstituteStatus | null;
	/** Session the current `instituteStatus` belongs to (prevents cross-session UI flicker). */
	instituteStatusSessionId: string | number | null;
	recentApplicationData: RecentApplicationData | null;
	showRenewalModal: boolean;
}

const initialState: ConfigState = {
	selectedMenuItem: REGISTER_ACTION_MENU_KEY,
	applicationStatus: ApplicationStatus.NOT_REGISTERED,
	applicationId: 0,
	applicationUuid: null,
	certifiedApplicationUuid: null,
	challanId: null,
	challanAmount: null,
	challanResponse: null,
	selectedInstituteId: null,
	selectedSessionId: null,
	instituteStatus: null,
	instituteStatusSessionId: null,
	recentApplicationData: null,
	showRenewalModal: false,
};

const configSlice = createSlice({
	name: "config",
	initialState,
	reducers: {
		setSelectedMenuItem(state, action: PayloadAction<string>) {
			state.selectedMenuItem = action.payload;
		},
		setApplicationStatus(state, action: PayloadAction<ApplicationStatus>) {
			state.applicationStatus = action.payload;
		},
		setApplicationId(state, action: PayloadAction<number>) {
			state.applicationId = action.payload;
		},
		setApplicationUuid(state, action: PayloadAction<string>) {
			state.applicationUuid = action.payload;
		},
		setCertifiedApplicationUuid(state, action: PayloadAction<string | null>) {
			state.certifiedApplicationUuid = action.payload;
		},
		setChallanData(state, action: PayloadAction<{ id: number; amount: number; response?: ChallanData }>) {
			state.challanId = action.payload.id;
			state.challanAmount = action.payload.amount;
			state.challanResponse = action.payload.response || null;
		},
		setSelectedInstituteId(state, action: PayloadAction<string | number | null>) {
			state.selectedInstituteId = action.payload;
		},
		setSelectedSessionId(state, action: PayloadAction<string | number | null>) {
			const nextSessionId = action.payload;
			const sessionChanged =
				String(state.selectedSessionId ?? "") !== String(nextSessionId ?? "");

			state.selectedSessionId = nextSessionId;

			// Drop previous session's status immediately so UI cannot show CERTIFIED
			// (or any other status) for a newly selected year while the new fetch is in flight.
			if (sessionChanged) {
				state.instituteStatus = null;
				state.instituteStatusSessionId = null;
			}
		},
		setInstituteStatus(state, action: PayloadAction<Partial<InstituteStatus>>) {
			// Merge with existing state instead of replacing, to support partial updates
			if (state.instituteStatus) {
				state.instituteStatus = { ...state.instituteStatus, ...action.payload };
			} else {
				state.instituteStatus = action.payload as InstituteStatus;
			}
		},
		replaceInstituteStatus(
			state,
			action: PayloadAction<{
				data: InstituteStatus | null;
				sessionId?: string | number | null;
			}>,
		) {
			const { data: incoming, sessionId } = action.payload;

			if (!incoming) {
				state.instituteStatus = null;
				state.instituteStatusSessionId = sessionId ?? null;
				return;
			}

			// Ignore stale responses for a session the user already left
			if (
				sessionId != null &&
				state.selectedSessionId != null &&
				String(sessionId) !== String(state.selectedSessionId)
			) {
				return;
			}

			const current = state.instituteStatus;
			// Stale certified / other-session responses must not replace an active renew draft
			if (
				current &&
				current.status === APPLICATION_STATUS.DRAFT &&
				current.applicationType === APPLICATION_TYPE.RENEW &&
				incoming.status === APPLICATION_STATUS.CERTIFIED &&
				incoming.applicationId !== current.applicationId
			) {
				return;
			}

			state.instituteStatus = incoming;
			if (sessionId != null) {
				state.instituteStatusSessionId = sessionId;
			}
		},
		clearInstituteStatus(state) {
			state.instituteStatus = null;
			state.instituteStatusSessionId = null;
		},
		setRecentApplicationData(state, action: PayloadAction<RecentApplicationData>) {
			state.recentApplicationData = action.payload;
		},
		clearRecentApplicationData(state) {
			state.recentApplicationData = null;
			state.applicationUuid = null;
		},
		setShowRenewalModal(state, action: PayloadAction<boolean>) {
			state.showRenewalModal = action.payload;
		},
		resetConfig(state) {
			state.applicationStatus = ApplicationStatus.NOT_REGISTERED;
			state.applicationId = null;
			state.challanId = null;
			state.challanAmount = null;
			state.applicationUuid = null;
			state.certifiedApplicationUuid = null;
			state.challanResponse = null;
			state.selectedInstituteId = null;
			state.selectedSessionId = null;
			state.instituteStatus = null;
			state.instituteStatusSessionId = null;
			state.recentApplicationData = null;
			state.showRenewalModal = false;
			// Keep selectedMenuItem as it's for UI navigation
		},
	},
});

export const {
	setSelectedMenuItem,
	setApplicationStatus,
	setApplicationId,
	setApplicationUuid,
	setCertifiedApplicationUuid,
	setChallanData,
	setSelectedInstituteId,
	setSelectedSessionId,
	resetConfig,
	setInstituteStatus,
	replaceInstituteStatus,
	clearInstituteStatus,
	setRecentApplicationData,
	clearRecentApplicationData,
	setShowRenewalModal,
} = configSlice.actions;
export default configSlice.reducer;
